import React, { useState, useEffect, useRef } from "react";
import { sendApprovalEmail, sendRejectionEmail } from "../Actions/SubmissionStatusEmail";
import "../../assets/Approval.css";
import { FaEye, FaCheck, FaTimes, FaExternalLinkAlt, FaRedo } from "react-icons/fa";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  deleteField,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const getStatusBadge = (status) => {
  if (status === "Approved")
    return <span className="status-badge approved">Approved</span>;
  if (status === "Pending")
    return <span className="status-badge pending">Pending</span>;
  if (status === "Rejected")
    return <span className="status-badge rejected">Rejected</span>;
  if (status === "Resubmitted")
    return <span className="status-badge resubmitted">Resubmitted</span>;
  return <span className="status-badge unknown">Unknown</span>;
};

const Approval = () => {
  const [submissions, setSubmissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const submissionsRef = useRef(new Map()); // Track submissions for change detection

  // Function to handle resubmission logic
  
  const handleResubmission = async (updatedDoc) => {
    try {
      const docRef = doc(db, "answers", updatedDoc.id);
      
      // Remove status field and update timestamp to treat as new submission
      await updateDoc(docRef, {
        status: deleteField(),
        submittedAt: serverTimestamp(),
        resubmittedAt: serverTimestamp(),
        isResubmission: true,
        previousStatus: "Rejected"
      });

      // Also update the course document if exists
      if (updatedDoc.courseId && updatedDoc.dayId) {
        await setDoc(
          doc(db, "courses", updatedDoc.courseId, "day", updatedDoc.dayId),
          { 
            status: deleteField(),
            resubmittedAt: serverTimestamp(),
            isResubmission: true
          },
          { merge: true }
        );
      }

      
    } catch (error) {
      
    }
  };

  // Listen for real-time updates to detect document changes
  useEffect(() => {
    const q = query(collection(db, "answers"), orderBy("submittedAt", "desc"));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const data = querySnapshot.docs.map((doc) => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          status: docData.status || "Pending",
        };
      });

      // Check for updates in existing rejected documents
      data.forEach(async (currentDoc) => {
        const previousDoc = submissionsRef.current.get(currentDoc.id);
        
        if (previousDoc && 
            previousDoc.status === "Rejected" && 
            currentDoc.status === "Rejected") {
          
          // Check if any content fields have been updated
          
          const contentFields = ['githubRepoUrl', 'commitCode', 'description', 'answer'];
          const hasContentChanged = contentFields.some(field => 
            currentDoc[field] !== previousDoc[field] && 
            currentDoc[field] !== undefined && 
            currentDoc[field] !== null &&
            currentDoc[field] !== ""
          );

          // Also check if submittedAt timestamp has been updated recently (within last 10 seconds)
          const currentTime = new Date().getTime();
          const docTime = currentDoc.submittedAt?.seconds 
            ? currentDoc.submittedAt.seconds * 1000 
            : new Date(currentDoc.submittedAt).getTime();
          const timeDifference = currentTime - docTime;
          const isRecentlyUpdated = timeDifference < 10000; // 10 seconds

          if (hasContentChanged || isRecentlyUpdated) {
            //console.log(`Document ${currentDoc.id} has been updated after rejection - treating as resubmission`);
            await handleResubmission(currentDoc);
          }
        }
      });

      // Update the ref with current data
      submissionsRef.current.clear();
      data.forEach(doc => {
        submissionsRef.current.set(doc.id, { ...doc });
      });

      setSubmissions(data);
    }, (error) => {
      console.error("Error listening to submissions:", error);
    });

    return () => unsubscribe();
  }, []);

  // Initial fetch - keeping the original useEffect as backup
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "answers"));
        const data = querySnapshot.docs.map((doc) => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            status: docData.status || "Pending",
          };
        });
        
        // Initialize the ref
        submissionsRef.current.clear();
        data.forEach(doc => {
          submissionsRef.current.set(doc.id, { ...doc });
        });
        
        setSubmissions(data);
      } catch (error) {
        console.error("Error fetching submissions:", error);
      }
    };
    
    fetchSubmissions();
  }, []);

  const handleView = (submission, idx) => {
    setSelectedSubmission(submission);
    setSelectedIndex(idx);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubmission(null);
    setSelectedIndex(null);
  };

const handleApprove = async (submission, idx) => {
  if (typeof idx === "number") {
    const updated = [...submissions];
    updated[idx] = { ...updated[idx], status: "Approved", approvedAt: new Date() };
    setSubmissions(updated);
    
    const submissionId = submission.id;
    if (submissionId) {
      try {
        // Update Firestore documents
        await updateDoc(doc(db, "answers", submissionId), {
          status: "Approved",
          approvedAt: serverTimestamp(),
        });
        
        if (submission.courseId && submission.dayId) {
          await setDoc(
            doc(db, "courses", submission.courseId, "day", submission.dayId),
            { 
              status: "Approved",
              approvedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        // Send approval email to student
        const emailResult = await sendApprovalEmail(
          submission, 
          `Great work on your ${submission.courseTitle} - Day ${submission.dayId} submission! Your solution demonstrates excellent understanding of the concepts.`
        );
        
        if (emailResult.success) {
          //console.log('Approval email sent successfully:', emailResult.emailId);
        } else {
          console.error('Failed to send approval email:', emailResult.error);
        }
        
      } catch (error) {
        console.error("Error updating submission or sending email:", error);
      }
    }
  }
  handleCloseModal();
};

  const handleReject = async (submission, idx) => {
  if (typeof idx === "number") {
    const updated = [...submissions];
    updated[idx] = { ...updated[idx], status: "Rejected", rejectedAt: new Date() };
    setSubmissions(updated);
    
    const submissionId = submission.id;
    if (submissionId) {
      try {
        // Update Firestore documents
        await updateDoc(doc(db, "answers", submissionId), {
          status: "Rejected",
          rejectedAt: serverTimestamp(),
        });
        
        if (submission.courseId && submission.dayId) {
          await setDoc(
            doc(db, "courses", submission.courseId, "day", submission.dayId),
            { 
              status: "Rejected",
              rejectedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        // Send rejection email to student
        const emailResult = await sendRejectionEmail(
          submission,
          `Your submission for ${submission.courseTitle} - Day ${submission.dayId} needs some improvements. Please review the requirements carefully and resubmit. Don't hesitate to reach out if you need clarification on any concepts.`
        );
        
        if (emailResult.success) {
          //console.log('Rejection email sent successfully:', emailResult.emailId);
        } else {
          console.error('Failed to send rejection email:', emailResult.error);
        }
        
      } catch (error) {
        console.error("Error updating submission or sending email:", error);
      }
    }
  }
  handleCloseModal();
};
  // Manual resubmission function (optional - for admin to manually reset)
  const handleManualResubmission = async (submission, idx) => {
    if (typeof idx === "number") {
      await handleResubmission(submission);
      handleCloseModal();
    }
  };

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">Approval Dashboard</h2>
      <p className="dashboard-subtitle">
        Review and approve student submissions
      </p>

      <div className="card-box">
        <div className="card-header">
          <h5 className="card-title">Student Submissions</h5>
          <p className="card-subtitle">
            Review and manage student assignment submissions
          </p>
        </div>

        <input
          type="text"
          className="search-input"
          placeholder="Search submissions..."
        />

        <div className="table-responsive">
          <table className="submissions-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Course</th>
                <th>Day</th>
                <th>Submitted</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions
                .slice()
                .sort((a, b) => {
                  // Sort by submittedAt with newest first
                  const aTime = a.submittedAt?.seconds
                    ? a.submittedAt.seconds
                    : new Date(a.submittedAt).getTime() / 1000;
                  const bTime = b.submittedAt?.seconds
                    ? b.submittedAt.seconds
                    : new Date(b.submittedAt).getTime() / 1000;
                  return bTime - aTime;
                })
                .map((item, idx) => (
                  <tr key={item.id || idx} className={item.isResubmission ? 'resubmission-row' : ''}>
                    <td>
                      {item.studentName}
                      {item.isResubmission && (
                        <span className="resubmission-indicator" title="Resubmitted">
                          <FaRedo style={{ marginLeft: 5, fontSize: 12, color: '#007bff' }} />
                        </span>
                      )}
                    </td>
                    <td>{item.courseTitle}</td>
                    <td>{item.dayId}</td>
                    <td>
                      {item.submittedAt
                        ? new Date(
                            item.submittedAt.seconds
                              ? item.submittedAt.seconds * 1000
                              : item.submittedAt
                          ).toLocaleString()
                        : ""}
                    </td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td className="actions-column">
                      <FaEye
                        className="icon eye"
                        onClick={() => handleView(item, idx)}
                        style={{ cursor: "pointer" }}
                        title="View Details"
                      />
                      {item.status === "Pending" && (
                        <>
                          <FaCheck
                            className="icon approve"
                            onClick={() => handleApprove(item, idx)}
                            style={{ cursor: "pointer" }}
                            title="Approve"
                          />
                          <FaTimes
                            className="icon reject"
                            onClick={() => handleReject(item, idx)}
                            style={{ cursor: "pointer" }}
                            title="Reject"
                          />
                        </>
                      )}
                      {item.status === "Rejected" && (
                        <FaRedo
                          className="icon resubmit"
                          onClick={() => handleManualResubmission(item, idx)}
                          style={{ cursor: "pointer", color: "#007bff" }}
                          title="Reset as New Submission"
                        />
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="footer-section">
          <span>
            Showing 1 to {submissions.length} of {submissions.length} entries
          </span>
          <div className="pagination-box">
            <button className="pagination-btn active">1</button>
          </div>
        </div>
      </div>

      {/* Modal Dialog */}
      {showModal && selectedSubmission && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.2)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal-dialog"
            style={{
              maxWidth: 600,
              width: "100%",
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
              overflow: "hidden",
            }}
          >
            <div
              className="modal-content"
              style={{ border: "none", borderRadius: 10 }}
            >
              <div
                className="modal-header"
                style={{
                  borderBottom: "1px solid #eee",
                  padding: "20px 24px 12px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontWeight: 600, fontSize: 20 }}
                >
                  Submission Details
                  {selectedSubmission.isResubmission && (
                    <span style={{ 
                      marginLeft: 10, 
                      fontSize: 14, 
                      color: "#007bff",
                      fontWeight: 400 
                    }}>
                      (Resubmitted)
                    </span>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseModal}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 22,
                    opacity: 0.7,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
              <div
                className="modal-body"
                style={{ padding: "16px 24px 0 24px" }}
              >
                <div style={{ fontWeight: 500, marginBottom: 8, fontSize: 16 }}>
                  {selectedSubmission.studentName}'s submission for{" "}
                  {selectedSubmission.courseTitle} - {selectedSubmission.dayId}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 15 }}>Status</div>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 12px",
                      borderRadius: 12,
                      fontSize: 13,
                      fontWeight: 500,
                      background:
                        selectedSubmission.status === "Pending"
                          ? "#f5f5f5"
                          : selectedSubmission.status === "Approved"
                          ? "#e6f4ea"
                          : "#fbeaea",
                      color:
                        selectedSubmission.status === "Pending"
                          ? "#444"
                          : selectedSubmission.status === "Approved"
                          ? "#1a7f37"
                          : "#d32f2f",
                      border:
                        selectedSubmission.status === "Pending"
                          ? "1px solid #ddd"
                          : "none",
                      marginTop: 4,
                    }}
                  >
                    {selectedSubmission.status}
                  </span>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 500, fontSize: 15 }}>Submitted</div>
                  <span style={{ color: "#444", fontSize: 14 }}>
                    {selectedSubmission.submittedAt
                      ? new Date(
                          selectedSubmission.submittedAt.seconds
                            ? selectedSubmission.submittedAt.seconds * 1000
                            : selectedSubmission.submittedAt
                        ).toLocaleString()
                      : ""}
                  </span>
                </div>
                {selectedSubmission.resubmittedAt && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 500, fontSize: 15 }}>Resubmitted</div>
                    <span style={{ color: "#007bff", fontSize: 14 }}>
                      {new Date(
                        selectedSubmission.resubmittedAt.seconds
                          ? selectedSubmission.resubmittedAt.seconds * 1000
                          : selectedSubmission.resubmittedAt
                      ).toLocaleString()}
                    </span>
                  </div>
                )}
                <div style={{ fontWeight: 500, marginBottom: 4, fontSize: 15 }}>
                  Answer
                </div>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 8,
                    background: "#fafbfc",
                    padding: 16,
                    fontSize: 15,
                    color: "#222",
                    marginBottom: 16,
                    minHeight: 80,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  }}
                >
                  <div style={{ marginBottom: 10 }}>
                    <strong style={{ width: 140, display: "inline-block" }}>GitHub Repo URL:</strong>
                    {selectedSubmission.githubRepoUrl ? (
                      <a
                        href={selectedSubmission.githubRepoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: 4,
                          color: "#0366d6",
                          textDecoration: "underline",
                          display: "inline-flex",
                          alignItems: "center",
                          wordBreak: "break-word",
                        }}
                      >
                        {selectedSubmission.githubRepoUrl}
                        <FaExternalLinkAlt style={{ marginLeft: 6, fontSize: 13 }} />
                      </a>
                    ) : (
                      <span style={{ marginLeft: 8, color: "#999" }}>-</span>
                    )}
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <strong style={{ width: 140, display: "inline-block" }}>Commit Code:</strong>
                    <span>{selectedSubmission.commitCode || "-"}</span>
                  </div>

                  <div>
                    <strong style={{ width: 140, display: "inline-block" }}>Description:</strong>
                    <span>{selectedSubmission.description || "No Description added"}</span>
                  </div>
                </div>
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: "none",
                  padding: "20px 24px 20px 24px",
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 16,
                }}
              >
                <button
                  className="btn btn-dark"
                  style={{
                    minWidth: 120,
                    fontWeight: 500,
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                  onClick={() =>
                    handleApprove(selectedSubmission, selectedIndex)
                  }
                  disabled={selectedSubmission.status !== "Pending"}
                >
                  <FaCheck />
                  Approve
                </button>
                <button
                  className="btn btn-outline-dark"
                  style={{
                    minWidth: 120,
                    fontWeight: 500,
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                  onClick={() =>
                    handleReject(selectedSubmission, selectedIndex)
                  }
                  disabled={selectedSubmission.status !== "Pending"}
                >
                  <FaTimes />
                  Reject
                </button>
                {selectedSubmission.status === "Rejected" && (
                  <button
                    className="btn btn-outline-primary"
                    style={{
                      minWidth: 120,
                      fontWeight: 500,
                      fontSize: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                    onClick={() =>
                      handleManualResubmission(selectedSubmission, selectedIndex)
                    }
                  >
                    <FaRedo />
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approval;
